import { testIds } from "@/testIds";
import type { TableData } from "@/components/editor/table-grid/tableModel";
import type { FigureColours } from "@/lib/tikzAuthoring";
import {
  columnCount,
  columnRef,
  columnSummary,
  headerText,
  labelText,
  numericColumns,
  plotData,
  yLabelSource,
  type LegendCorner,
  type PlotSpec,
  type PlotType,
} from "@/lib/tikzPlot";
import styles from "./PlotSettings.module.scss";

const T = testIds.plot;
const TYPES: Array<{ value: PlotType; label: string }> = [
  { value: "line", label: "Line" },
  { value: "scatter", label: "Scatter" },
  { value: "bar", label: "Bar" },
];

export interface PlotSettingsProps {
  table: TableData;
  spec: PlotSpec;
  /** `immediate`: a click (draw now); otherwise typing (draw after the pause). */
  onChange: (spec: PlotSpec, immediate: boolean) => void;
  /** The theme's colours, for the series swatches (lilia-seq1…). */
  colours?: FigureColours;
  /** Where the legend went in the last source written. */
  legendCorner?: LegendCorner | null;
  /** The table was removed: the settings are shown, not changed. */
  readOnly?: boolean;
}

/**
 * The plot's settings (README-step4, 1b): Chart, x axis, Series, Axis labels,
 * Legend. The same panel is the create dialog's left side and the split view's
 * Plot tab. Clicks redraw at once; typing in a label redraws after the pause.
 */
export function PlotSettings({ table, spec, onChange, colours, legendCorner, readOnly }: PlotSettingsProps) {
  const n = columnCount(table);
  const numeric = new Set(numericColumns(table));
  const xIsText = !numeric.has(spec.x.column);
  const checked = new Set(spec.series.map((s) => s.column));
  const name = (c: number) => headerText(table, c) || `Column ${c + 1}`;
  const data = plotData(table, { ...spec, xLabel: null, yLabel: null });
  const generated = typeof data === "object" ? data : null;
  const ySource = yLabelSource(table, spec);

  const set = (next: Partial<PlotSpec>, immediate = true) => onChange({ ...spec, ...next }, immediate);

  const chooseX = (c: number) => {
    const series = spec.series.filter((s) => s.column !== c);
    set({ x: columnRef(table, c), series, ...(numeric.has(c) ? {} : { type: "bar" as PlotType }) });
  };

  const toggleSeries = (c: number) => {
    const next = checked.has(c) ? [...checked].filter((x) => x !== c) : [...checked, c];
    set({ series: next.sort((a, b) => a - b).map((x) => columnRef(table, x)) });
  };

  // Colours by position among the plotted series, as the source writes them.
  const seqOf = (c: number) => {
    const i = spec.series.findIndex((s) => s.column === c);
    return i < 0 ? null : `lilia-seq${(i % 8) + 1}`;
  };

  return (
    <div className={styles.panel} data-testid={T.settings} aria-disabled={readOnly || undefined}>
      <section className={styles.group}>
        <h4 className={styles.label}>Chart</h4>
        <div className={styles.segmented} role="radiogroup" aria-label="Chart">
          {TYPES.map((t) => {
            const disabled = readOnly || (xIsText && t.value !== "bar");
            return (
              <button
                key={t.value}
                type="button"
                role="radio"
                aria-checked={spec.type === t.value}
                data-testid={T.type}
                data-value={t.value}
                disabled={disabled}
                className={styles.segment}
                onClick={() => set({ type: t.value })}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        {xIsText && <p className={styles.note}>{name(spec.x.column)} is text, so the plot uses bars.</p>}
      </section>

      <section className={styles.group} data-testid={T.x}>
        <h4 className={styles.label}>x axis</h4>
        <div className={styles.chips}>
          {Array.from({ length: n }, (_, c) => (
            <button
              key={c}
              type="button"
              className={styles.chip}
              data-testid={T.xOption}
              data-column={c}
              aria-pressed={spec.x.column === c}
              disabled={readOnly}
              onClick={() => chooseX(c)}
            >
              {name(c)}
            </button>
          ))}
        </div>
      </section>

      <section className={styles.group} data-testid={T.series}>
        <h4 className={styles.label}>
          Series <span className={styles.hint}>number columns are ticked</span>
        </h4>
        <ul className={styles.seriesList}>
          {Array.from({ length: n }, (_, c) => c)
            .filter((c) => c !== spec.x.column && numeric.has(c))
            .map((c) => {
              const sum = columnSummary(table, c);
              const empty = sum.values === 0;
              const seq = seqOf(c);
              const meta = empty
                ? "no numbers yet"
                : [
                    `${sum.values} ${sum.values === 1 ? "value" : "values"}`,
                    sum.values === 1 ? "drawn as a point" : null,
                    sum.unit,
                    sum.skipped ? `${sum.skipped} blank ${sum.skipped === 1 ? "cell" : "cells"} skipped` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ");
              return (
                <li key={c}>
                  <label
                    className={styles.series}
                    data-testid={T.seriesOption}
                    data-column={c}
                    data-checked={checked.has(c) || undefined}
                    data-disabled={empty || undefined}
                  >
                    <input type="checkbox" checked={checked.has(c)} disabled={readOnly || empty} onChange={() => toggleSeries(c)} />
                    <span className={styles.swatch} style={{ background: seq ? (colours?.[seq] ?? "var(--lilia-text-muted)") : "transparent" }} aria-hidden="true" />
                    <span className={styles.seriesText}>
                      <span className={styles.seriesName}>{name(c)}</span>
                      <span className={styles.meta}>{meta}</span>
                    </span>
                    {seq && <code className={styles.seq}>{seq}</code>}
                  </label>
                </li>
              );
            })}
        </ul>
      </section>

      <section className={styles.group}>
        <h4 className={styles.label}>
          Axis labels <span className={styles.hint}>· from the headers</span>
        </h4>
        <label className={styles.axis}>
          <span className={styles.axisName}>x</span>
          <input
            type="text"
            data-testid={T.axisLabelX}
            value={spec.xLabel ?? ""}
            placeholder={labelText(generated?.xLabel ?? "")}
            disabled={readOnly}
            onChange={(e) => set({ xLabel: e.target.value || null }, false)}
          />
          <span className={styles.axisTag}>{spec.xLabel ? "yours" : "header"}</span>
        </label>
        <label className={styles.axis}>
          <span className={styles.axisName}>y</span>
          <input
            type="text"
            data-testid={T.axisLabelY}
            value={spec.yLabel ?? ""}
            placeholder={labelText(generated?.yLabel ?? "")}
            disabled={readOnly}
            onChange={(e) => set({ yLabel: e.target.value || null }, false)}
          />
          <span className={styles.axisTag}>{spec.yLabel ? "yours" : (ySource ?? "")}</span>
        </label>
      </section>

      <section className={styles.group}>
        <label className={styles.toggle}>
          <input type="checkbox" role="switch" data-testid={T.legend} checked={spec.legend} disabled={readOnly} onChange={(e) => set({ legend: e.target.checked })} />
          <span className={styles.toggleText}>Legend</span>
          {spec.legend && legendCorner && <span className={styles.hint}>placed {legendCorner}</span>}
        </label>
      </section>
    </div>
  );
}
